<script setup>
import { reactive, onMounted, computed, ref, watch, onUnmounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useNumberFormat } from "@/composables/useNumberFormat";
import { useQuotesStore } from "@/stores/quotes";
import { usePeersStore } from "@/stores/peers";
import { useMoversStore } from "@/stores/movers";
import { useChartDataStore } from "@/stores/chart-data";
import { useCompanyInfoStore } from "@/stores/company-info";
import { registerEvents } from "@/composables/components";
import { getToolToken } from "@/apis/ums/auth";
import * as DATA$farm from "@/apis/data/farm";
import QSlider from "@/components/ui/QSlider.vue";
import QMiniChart from "@/components/ui/QMiniChart.vue";
import QMoon from "@/icons/QMoon.vue";
import QSunrise from "@/icons/QSunrise.vue";
import QExtendedHoursQuote from "@/components/ui/quotes/QExtendedHoursQuote.vue";
import QQuote from "@/components/ui/quotes/QQuote.vue";

const compName = "MiniQuotes";

const { t, locale } = useI18n();
const intlLocale = computed(() => (locale.value === "fr" ? "fr-CA" : "en-us"));
const { formatPrice } = useNumberFormat(locale.value);
const quotesStore = useQuotesStore();
const peersStore = usePeersStore();
const moversStore = useMoversStore();
const chartDataStore = useChartDataStore();
const companyInfoStore = useCompanyInfoStore();

// Initialize component-level authentication and events
const authToken = ref(null);

onMounted(async () => {
    registerEvents(compName);
    authToken.value = await getToolToken(compName);

    await fetchQuoteData();
});

const props = defineProps({
    /**
     * Stock symbols to display data for (custom symbols)
     * Can be a comma-separated string or an array of objects with symbol and optional label
     * @example "MSFT,AAPL,GOOGL,TSLA" or [{symbol: "MSFT", label: "Microsoft"}, {symbol: "AAPL", label: "Apple Inc"}]
     */
    symbols: {
        type: [String, Array],
        default: "",
    },

    /**
     * Peer symbol to fetch data for (used with peers preset)
     */
    peerSymbol: {
        type: String,
        default: "MSFT",
    },

    /**
     * Preset data to display in the slider for a specific symbol (for peers, use with peerSymbol)
     * @values peers, movers
     */
    preset: {
        type: String,
        default: undefined,
    },

    /**
     * Number of quotes visible at once in the slider
     */
    slidesPerView: {
        type: [Number, String],
        default: "auto",
    },

    /**
     * Number of quotes to scroll at once
     */
    slidesPerGroup: {
        type: Number,
        default: 1,
    },

    /**
     * Auto play configuration
     */
    autoplay: {
        type: [Boolean],
        default: false,
    },

    /**
     * Delay in milliseconds between auto play slides
     */
    autoplayDelay: {
        type: Number,
        default: 2000,
    },

    /**
     * Circular navigation for the slider
     */
    loop: {
        type: Boolean,
        default: false,
    },

    /**
     * Show navigation arrows
     */
    navigation: {
        type: Boolean,
        default: true,
    },

    /**
     * Show indicator dots
     */
    pagination: {
        type: Boolean,
        default: false,
    },

    /**
     * Responsive options for the slider
     * You can pass in any custom breakpoints you want
     */
    breakpoints: {
        type: Object,
        default: () => ({
            1500: { slidesPerView: 6, slidesPerGroup: 6 },
            1200: { slidesPerView: 5, slidesPerGroup: 5 },
            992: { slidesPerView: 4, slidesPerGroup: 4 },
            768: { slidesPerView: 3, slidesPerGroup: 3 },
            468: { slidesPerView: 2, slidesPerGroup: 2 },
            320: { slidesPerView: 1, slidesPerGroup: 1 },
        }),
    },

    /**
     * Space between slides (in pixels)
     */
    spaceBetween: {
        type: Number,
        default: 8,
    },

    /**
     * Limit the number of quotes to display (for preset data)
     */
    limit: {
        type: Number,
        default: 10,
    },

    /**
     * Use NAICS codes to fetch data for peers (used with peers preset)
     */
    useNaics: {
        type: Boolean,
        default: false,
    },

    /**
     * Stat to pull data for (used with movers preset)
     * @values va, dv, dg, dl, pg, pl, ah, al, plvc
     */
    stat: {
        type: String,
        default: "va",
    },

    /**
     * Country code to fetch data for (used with movers preset)
     * @values US, CA
     */
    statCountry: {
        type: String,
        default: "US",
    },

    /**
     * Exchange code to filter data for (used with movers preset)
     * @values NYSE, NSD, AMX, OTO, TSX, TSV, CNQ, PURE, ALPHA, AQNC, AQL, LSE
     */
    statExchange: {
        type: String,
        default: "",
    },

    /**
     * Show a chart for the quote
     * @values true, false
     */
    showChart: {
        type: Boolean,
        default: false,
    },

    /**
     * Chart layout position
     * @values bottom, right, top, inside
     */
    chartPosition: {
        type: String,
        default: "bottom",
        validator: value => ["bottom", "right", "top", "inside"].includes(value),
    },

    /**
     * Chart interval (minutes)
     * @values 1, 5, 15, 30, 60
     */
    chartInterval: {
        type: Number,
        default: 15,
        validator: value => [1, 5, 15, 30, 60].includes(value),
    },

    /**
     * Include peer symbol in the peers preset
     * @values true, false
     */
    includePeerSymbol: {
        type: Boolean,
        default: false,
    },

    /**
     * URL to redirect to when a quote is clicked, with symbol and datatype query params appended
     * @example "/details" becomes "/details?symbol=MSFT&datatype=Equity"
     */
    forwardURL: {
        type: String,
        default: undefined,
    },

    /**
     * Show a triangle icon indicating price direction
     */
    showChangeIcon: {
        type: Boolean,
        default: false,
    },

    /**
     * Security type(s) to fetch peer data for (used with peers preset)
     * Can be a comma-separated string or an array of security types
     * @values AD, RE, CE, GD, SI, LP, CS, ET, BO, PS, SU, WT
     */
    securityType: {
        type: [String, Array],
        default: "CS,AD",
    },

    /**
     * Market cap size to filter symbols by (used with movers preset)
     * @values , large, mid, small, micro
     */
    marketCapSize: {
        type: String,
        default: undefined,
    },

    /**
     * Include extended hours trading sessions (pre-market and after-hours)
     * When false, only shows regular market session data
     * @values true, false
     */
    extendedHours: {
        type: Boolean,
        default: false,
    },

    /**
     * Field to sort quotes by (for custom symbols or peers preset)
     * @values , symbol, longName, last, change, changePercent, volume, marketCap
     */
    sortField: {
        type: String,
        default: undefined,
        validator: value =>
            [
                undefined,
                "symbol",
                "longName",
                "last",
                "change",
                "changePercent",
                "volume",
                "marketCap",
            ].includes(value),
    },

    /**
     * Sort order for quotes (for custom symbols or peers preset)
     * @values asc, desc
     */
    sortOrder: {
        type: String,
        default: "desc",
        validator: value => ["asc", "desc"].includes(value),
    },

    /**
     * Always show company long name instead of symbol when in "right" chart position
     */
    alwaysShowLongName: {
        type: Boolean,
        default: false,
    },

    /**
     * Show the currency symbol (e.g., $) in the price display
     */
    showCurrencySymbol: {
        type: Boolean,
        default: false,
    },
});

const state = reactive({
    isLoading: true,
    noDataFound: false,
    symbolsWithoutChartData: new Set(),
});

const data = reactive({
    quotes: [],
});

const symbolsArray = computed(() => {
    if (Array.isArray(props.symbols)) {
        // Handle structured format [{symbol: "MSFT", label: "Custom"}, ...]
        return props.symbols.map(item =>
            typeof item === "object" && item.symbol
                ? item.symbol.trim().toUpperCase()
                : String(item).trim().toUpperCase()
        );
    } else {
        // Handle string format "MSFT,AAPL,GOOGL"
        return props.symbols.split(",").map(symbol => symbol.trim().toUpperCase());
    }
});

// Get the symbol to use for peers preset - use peerSymbol if provided, otherwise fallback to first symbol
const primarySymbol = computed(() => {
    if (props.peerSymbol) {
        return props.peerSymbol.trim().toUpperCase();
    }
    return symbolsArray.value[0] || "";
});

// Determine if we need to fetch peers (peers) or movers data
const isPeersPreset = computed(() => props.preset === "peers");
const isMoversPreset = computed(() => props.preset === "movers");

// Handle security types as array or comma-separated string
const securityTypesArray = computed(() => {
    if (Array.isArray(props.securityType)) {
        return props.securityType.map(type => type.trim().toUpperCase());
    } else {
        return props.securityType.split(",").map(type => type.trim().toUpperCase());
    }
});

// Convert security types array to comma-separated string for API
const securityTypesString = computed(() => {
    return securityTypesArray.value.join(",");
});

const fetchQuoteData = async () => {
    try {
        state.isLoading = true;
        state.noDataFound = false;
        state.symbolsWithoutChartData.clear();
        let symbolsToFetch = []; // Step 1: Get the list of symbols to fetch based on configuration
        const hasCustomSymbols = Array.isArray(props.symbols)
            ? props.symbols.length > 0
            : props.symbols.trim() !== "";

        if (symbolsArray.value.length > 0 && hasCustomSymbols) {
            console.log("Fetching quotes for custom symbols:", symbolsArray.value);
            symbolsToFetch = symbolsArray.value;
            await quotesStore.fetch(symbolsToFetch.join(","), authToken.value, props.extendedHours);
            data.quotes = quotesStore.getQuotesBySymbols(
                props.symbols, // Pass the original symbols prop to preserve label info
                props.extendedHours,
                intlLocale.value
            );
        } else if (isPeersPreset.value && primarySymbol.value) {
            const fetchLimit = !props.includePeerSymbol ? props.limit + 1 : props.limit;
            const peerQuotes = await peersStore.fetchPeersWithQuotes(
                primarySymbol.value,
                authToken.value,
                fetchLimit,
                props.useNaics,
                securityTypesString.value,
                false
            );

            // Filter out the main peer symbol if needed
            if (!props.includePeerSymbol) {
                data.quotes = peerQuotes
                    .filter(quote => quote.symbol !== primarySymbol.value)
                    .slice(0, props.limit);
            } else {
                data.quotes = peerQuotes;
            }

            symbolsToFetch = data.quotes.map(quote => quote.symbol);
        } else if (isMoversPreset.value) {
            const moversData = await moversStore.fetchMovers(
                props.stat,
                authToken.value,
                props.statCountry,
                props.extendedHours,
                props.marketCapSize,
                props.statExchange,
                props.limit
            );

            data.quotes =
                props.limit > 0 && moversData.length > props.limit
                    ? moversData.slice(0, props.limit)
                    : moversData;
            symbolsToFetch = data.quotes.map(quote => quote.symbol);
        }

        // Step 2: For presets (peers/movers), fetch quotes from the quotes store
        if (symbolsToFetch.length > 0 && (isPeersPreset.value || isMoversPreset.value)) {
            // Fetch quotes with extended hours if needed
            console.log("Fetched quotes:", data.quotes);
            await quotesStore.fetch(symbolsToFetch.join(","), authToken.value, props.extendedHours);
            data.quotes = quotesStore.getQuotesBySymbols(
                symbolsToFetch,
                props.extendedHours,
                intlLocale.value
            );
        }

        // If we have no quotes after the initial fetch, set noDataFound and return early
        if (data.quotes.length === 0) {
            state.noDataFound = true;
            state.isLoading = false;
            return;
        }

        // Step 3: If charts are enabled, fetch chart data for visualization only
        if (props.showChart && symbolsToFetch.length > 0) {
            const chartParams = {
                interval: props.chartInterval,
                marketSession: props.extendedHours ? "all" : "mkt",
            };

            // Fetch chart data (for chart display only, not for quote information)
            await chartDataStore.fetchMultipleChartData(
                symbolsToFetch,
                authToken.value,
                chartParams
            );

            // Check which symbols have chart data available
            symbolsToFetch.forEach(symbol => {
                const chartData = chartDataStore.getFormattedChartData(symbol, chartParams);
                if (!chartData || chartData.series.length === 0 || chartData.pointCount === 0) {
                    state.symbolsWithoutChartData.add(symbol);
                }
            });
        }

        state.isLoading = false;
    } catch (error) {
        console.error("Error fetching data:", error);
        state.isLoading = false;
        state.noDataFound = true;
    }
};

/**
 * Sort the quotes data based on sortField and sortOrder props
 */
const sortedQuotes = computed(() => {
    if (!props.sortField || data.quotes.length === 0) {
        return data.quotes; // Return unsorted data if no sort field specified
    }

    return [...data.quotes].sort((a, b) => {
        let aValue = a[props.sortField];
        let bValue = b[props.sortField];

        if (props.sortField === "marketCap" || props.sortField === "volume") {
            aValue = parseFloat(aValue) || 0;
            bValue = parseFloat(bValue) || 0;
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
            return props.sortOrder === "asc"
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }

        if (props.sortOrder === "asc") {
            return aValue - bValue;
        } else {
            return bValue - aValue;
        }
    });
});

const autoplayConfig = computed(() => {
    if (props.autoplay === true) {
        return {
            delay: props.autoplayDelay,
            disableOnInteraction: false,
        };
    }

    return props.autoplay;
});

const handleQuoteClick = async symbol => {
    if (!props.forwardURL) return;

    try {
        const companyInfo = await companyInfoStore.fetchCompanyInfo(symbol, authToken.value);
        const instrumentType = (companyInfo?.instrumentType || "equity").toLowerCase();

        let url;

        if (props.forwardURL.startsWith("http")) {
            url = new URL(props.forwardURL);
        } else {
            url = new URL(props.forwardURL, window.location.origin);
        }

        url.searchParams.append("symbol", symbol);
        url.searchParams.append("datatype", instrumentType);

        window.location.href = url.toString();
    } catch (error) {
        console.error("Error navigating to symbol details:", error);
    }
};

watch(
    [
        () => props.symbols,
        () => props.preset,
        () => props.peerSymbol,
        () => props.stat,
        () => props.statCountry,
        () => props.statExchange,
        () => props.showChart,
        () => props.includePeerSymbol,
        () => props.limit,
        () => props.useNaics,
        () => props.chartInterval,
        () => props.securityType,
        () => props.marketCapSize,
        () => props.extendedHours,
    ],
    () => {
        fetchQuoteData();
    }
);

const hasChartData = symbol => {
    return !state.symbolsWithoutChartData.has(symbol);
};

// Track element overflow state - use ID to make it unique for each location
const longNameRefs = ref({});
const longNameOverflow = ref({});

// Check if text is overflowing in an element
const checkOverflow = async id => {
    await nextTick();
    const element = longNameRefs.value[id];
    if (element) {
        // More reliable overflow detection
        longNameOverflow.value[id] = element.scrollWidth > element.clientWidth;
    }
};

// Check overflow for all elements
const checkAllOverflows = async () => {
    await nextTick();
    for (const id in longNameRefs.value) {
        checkOverflow(id);
    }
};

onMounted(async () => {
    const resizeObserver = new ResizeObserver(() => {
        checkAllOverflows();
    });

    // Wait a moment for rendering to complete
    await nextTick();

    // Observe both individual items and the container for comprehensive detection
    const container = document.querySelector(".qmodii-miniquotes-content");
    if (container) {
        resizeObserver.observe(container);
    }

    const items = document.querySelectorAll(".qmodii-miniquotes-item");
    items.forEach(item => {
        resizeObserver.observe(item);
    });

    checkAllOverflows();

    onUnmounted(() => {
        resizeObserver.disconnect();
    });
});

const showExtendedHoursFooter = computed(() => {
    return (
        props.extendedHours &&
        formatQuotes.value.some(
            quote =>
                (quote.tradingSession === "premarket" && quote.premarket) ||
                (quote.tradingSession === "afterhours" && quote.afterhours)
        )
    );
});

const hasExtendedHoursData = quoteData => {
    return (
        (quoteData.premarket && quoteData.tradingSession === "premarket") ||
        (quoteData.afterhours && quoteData.tradingSession === "afterhours") ||
        // Also check for POST or PRE session from chart data
        quoteData.dataSource === "afterhours" ||
        quoteData.dataSource === "premarket"
    );
};

const chartMarketSession = computed(() => {
    return props.extendedHours ? "all" : "mkt";
});

const getChartClass = position => {
    return {
        "qmodii-miniquotes-chart-top": position === "top",
        "qmodii-miniquotes-chart-right": position === "right",
        "qmodii-miniquotes-chart-inside": position === "inside",
    };
};
</script>

<template lang="pug">
.qmodii-ui-tool.qmodii-miniquotes(:class="{ 'qmodii-loading': state.isLoading }")
    .qmodii-miniquotes-content(v-if="!state.isLoading && !state.noDataFound")
        QSlider(
            :value="sortedQuotes",
            :slidesPerView="props.slidesPerView",
            :slidesPerGroup="props.slidesPerGroup",
            :loop="props.loop",
            :autoplay="autoplayConfig",
            :navigation="props.navigation",
            :pagination="props.pagination",
            :breakpoints="props.breakpoints",
            :spaceBetween="props.spaceBetween"
        )
            template(#item="slotProps")
                .qmodii-miniquotes-item(
                    :class="{ 'qmodii-miniquotes-item-horizontal': props.showChart && props.chartPosition === 'right', 'qmodii-miniquotes-item-top-chart': props.showChart && props.chartPosition === 'top', 'qmodii-miniquotes-item-inside-chart': props.showChart && props.chartPosition === 'inside', 'qmodii-miniquotes-item-clickable': props.forwardURL }",
                    @click="props.forwardURL ? handleQuoteClick(slotProps.data.symbol) : null"
                )
                    //- Inside chart layout
                    template(v-if="props.showChart && props.chartPosition === 'inside'")
                        .qmodii-miniquotes-top.qmodii-miniquotes-inside-top
                            .qmodii-miniquotes-symbol(v-if="!props.alwaysShowLongName") {{ slotProps.data.symbol }}
                            .qmodii-miniquotes-name(
                                v-else="props.alwaysShowLongName",
                                ref="longNameRef",
                                v-tooltip="slotProps.data.longName"
                            ) {{ slotProps.data.longName }}
                            .qmodii-miniquotes-chart(:class="getChartClass('inside')")
                                QMiniChart(
                                    :symbol="slotProps.data.symbol",
                                    :chartInterval="props.chartInterval",
                                    :showNoDataMessage="!hasChartData(slotProps.data.symbol)",
                                    :marketSession="chartMarketSession"
                                )

                        .qmodii-miniquotes-inside-bottom
                            QQuote(
                                :data="slotProps.data",
                                :showChangeIcon="props.showChangeIcon",
                                layout="vertical"
                            )

                            QExtendedHoursQuote(
                                :data="slotProps.data",
                                :small="true",
                                :showChangeIcon="props.showChangeIcon",
                                :showDollarSign="props.showCurrencySymbol",
                                v-if="props.extendedHours"
                            )

                    //- Other layouts
                    template(v-else)
                        //- Top chart position
                        .qmodii-miniquotes-chart(
                            v-if="props.showChart && props.chartPosition === 'top'",
                            :class="getChartClass('top')"
                        )
                            QMiniChart(
                                :symbol="slotProps.data.symbol",
                                :chartInterval="props.chartInterval",
                                :showNoDataMessage="!hasChartData(slotProps.data.symbol)",
                                :marketSession="chartMarketSession"
                            )

                        .qmodii-miniquotes-data(
                            :class="{ 'qmodii-miniquotes-data-right-layout': props.showChart && props.chartPosition === 'right' }"
                        )
                            //- Standard Layout (bottom chart or no chart)
                            template(v-if="!(props.showChart && props.chartPosition === 'right')")
                                QQuote(
                                    :data="slotProps.data",
                                    :showChangeIcon="props.showChangeIcon",
                                    :showDollarSign="props.showCurrencySymbol",
                                    layout="grid"
                                )
                                    template(#symbol) {{ slotProps.data.symbol }}
                                    template(#name) {{ slotProps.data.longName }}

                                //- Extended hours quote
                                QExtendedHoursQuote(
                                    :data="slotProps.data",
                                    :showChangeIcon="props.showChangeIcon",
                                    :showDollarSign="props.showCurrencySymbol",
                                    v-if="props.extendedHours"
                                )

                            //- Right Layout (chart on right)
                            template(v-else)
                                QQuote(
                                    :data="slotProps.data",
                                    :showChangeIcon="props.showChangeIcon",
                                    :showDollarSign="props.showCurrencySymbol",
                                    :alwaysShowLongName="props.alwaysShowLongName",
                                    layout="vertical"
                                )
                                    template(#symbol) {{ slotProps.data.symbol }}
                                    template(#name) {{ slotProps.data.longName }}
                                    template(#price) {{ slotProps.data.last }}

                        //- Bottom or right chart position
                        .qmodii-miniquotes-chart(
                            v-if="props.showChart && props.chartPosition !== 'top' && props.chartPosition !== 'inside'",
                            :class="getChartClass(props.chartPosition)"
                        )
                            QMiniChart(
                                :symbol="slotProps.data.symbol",
                                :chartInterval="props.chartInterval",
                                :showNoDataMessage="!hasChartData(slotProps.data.symbol)",
                                :marketSession="chartMarketSession"
                            )

                        //- Extended hours
                        .qmodii-miniquotes-extended-hours-wrapper(
                            v-if="props.showChart && props.chartPosition === 'right' && props.extendedHours"
                        )
                            QExtendedHoursQuote(
                                :data="slotProps.data",
                                :showChangeIcon="props.showChangeIcon",
                                :showDollarSign="props.showCurrencySymbol"
                            )

    .qmodii-miniquotes-no-data(v-if="!state.isLoading && state.noDataFound")
        .qmodii-miniquotes-no-data-message
            .qmodii-miniquotes-no-data-icon ⚠️
            .qmodii-miniquotes-no-data-text
                p {{ t("No valid data found for the provided symbols") }}
                p.qmodii-miniquotes-no-data-symbols(v-if="symbolsArray.length > 0") 
                    span {{ t("Symbols") }}:&nbsp;
                    span.qmodii-miniquotes-no-data-symbol(
                        v-for="(symbol, index) in symbolsArray",
                        :key="index"
                    ) {{ symbol }}{{ index < symbolsArray.length - 1 ? ", " : "" }}
</template>

<style>
.qmodii-main {
    --qmodii-quote-positive-color: var(--qmodii-up-color);
    --qmodii-quote-negative-color: var(--qmodii-down-color);
}
</style>

<style lang="stylus" scoped>
.qmodii-ui-tool.qmodii-miniquotes
    container-type inline-size
    container-name miniquotes
    height fit-content

.qmodii-miniquotes-content
    height 100%
    display flex
    flex-direction column
    width 100%

.qmodii-miniquotes-swiper
    width 100%
    height 100%

.qmodii-miniquotes-item
    padding var(--qmodii-spacing-sm) var(--qmodii-spacing-md)
    height 100%
    border var(--qmodii-primary-border, 1px solid #e0e0e0)
    border-radius var(--qmodii-border-radius-md, 4px)
    background-color white
    display flex
    flex-direction column
    gap var(--qmodii-spacing-md)
    cursor: move;
    cursor: grab;
    cursor: -moz-grab;
    cursor: -webkit-grab;
    container-type inline-size
    container-name quote-item

    &:active
        cursor: grabbing;
        cursor: -moz-grabbing;
        cursor: -webkit-grabbing;

    &-clickable
        cursor pointer

        &:hover
            border-color var(--qmodii-primary)

    &-horizontal
        flex-direction row
        align-items stretch

        .qmodii-miniquotes-chart
            width 50%
            flex-grow 0
            flex-shrink 0

        .qmodii-miniquotes-data
            width 50%
            flex-shrink 0

        .qmodii-miniquotes-extended-hours-wrapper
            width 100%
            margin-top var(--qmodii-spacing-xs)
            order 3

    &-top-chart
        flex-direction column

    &-inside-chart
        flex-direction column

.qmodii-miniquotes-inside-top
    justify-content center
    margin-bottom var(--qmodii-spacing-sm)

    .qmodii-miniquotes-name
        flex unset
        font-weight bold

.qmodii-miniquotes-inside-bottom
    display flex
    flex-direction column
    align-items flex-start
    gap var(--qmodii-spacing-sm)

    .qmodii-quote-right
        text-align left
        justify-content flex-start

    .qmodii-extended-hours
        justify-content flex-start

.qmodii-miniquotes-data
    display flex
    flex-direction column
    gap var(--qmodii-spacing-sm)
    flex 1

.qmodii-miniquotes-data-right-layout
    display flex
    flex-direction column
    justify-content space-between
    gap var(--qmodii-spacing-sm)

.qmodii-miniquotes-header
    display flex
    align-items center
    gap var(--qmodii-spacing-sm)
    width 100%

    .qmodii-miniquotes-symbol
        font-weight bold
        white-space nowrap

    .qmodii-miniquotes-name
        white-space nowrap
        overflow hidden
        text-overflow ellipsis
        flex 1

.qmodii-miniquotes-header-name
    font-weight bold
    white-space nowrap
    overflow hidden
    text-overflow ellipsis
    flex 1

.qmodii-miniquotes-footer
    display flex
    width 100%

.qmodii-miniquotes-top
.qmodii-miniquotes-bottom
    display flex
    justify-content space-between
    align-items center
    width 100%
    gap var(--qmodii-spacing-sm)
    flex-wrap wrap

.qmodii-miniquotes-symbol
    font-weight bold

.qmodii-miniquotes-name
    white-space nowrap
    overflow hidden
    text-overflow ellipsis
    min-width 30%
    flex 1

.qmodii-miniquotes-bottom
    gap var(--qmodii-spacing-sm)

.qmodii-miniquotes-change
    text-align left
    display flex
    flex-wrap wrap

    &-positive
        color var(--qmodii-quote-positive-color)

    &-negative
        color var(--qmodii-quote-negative-color)

.qmodii-dorito
    display inline-block
    margin-left 3px

.qmodii-data-source
    display inline-block
    line-height 1
    display inline-flex
    align-items center
    vertical-align text-top

.qmodii-miniquotes-chart
    width 100%

    &-top
        width 100%
        margin-bottom var(--qmodii-spacing-sm)

    &-right
        width 50%

    &-inside
        width 100%
        margin-bottom 0

.qmodii-miniquotes-price
    position relative
    display inline-flex
    align-items center

@container miniquotes (max-width: 768px)
    .qmodii-miniquotes-item
        padding var(--qmodii-spacing-sm, 0.5em)

@container quote-item (width < 150px)
    .qmodii-miniquotes-name
        display none

    .qmodii-miniquotes-top,
    .qmodii-miniquotes-bottom
        flex-wrap wrap
        justify-content flex-start

    .qmodii-miniquotes-price
        width 100%
        text-align left
        margin-top 2px

    .qmodii-miniquotes-change
        width 100%
        font-size 0.9em
        margin-top 2px

.qmodii-miniquotes-no-data
    width 100%
    min-height 150px
    display flex
    align-items center
    justify-content center

.qmodii-miniquotes-no-data-message
    text-align center
    padding var(--qmodii-spacing-lg)
    width 100%
    max-width 500px
    margin auto
    background-color var(--qmodii-bg-warning-light, #fff8e1)
    border var(--qmodii-border-warning, 1px solid #ffecb3)
    border-radius var(--qmodii-border-radius-md, 4px)
    color var(--qmodii-text-color-dark, #333333)

.qmodii-miniquotes-no-data-icon
    margin-bottom var(--qmodii-spacing-sm)

.qmodii-miniquotes-no-data-text
    p
        margin var(--qmodii-spacing-xs) 0

.qmodii-miniquotes-no-data-symbols
    font-size var(--qmodii-text-sm)
    margin-top var(--qmodii-spacing-xs)

.qmodii-miniquotes-extended-hours
    display flex
    flex-wrap nowrap
    align-items center
    width 100%
    border-top var(--qmodii-primary-border)
    padding-top var(--qmodii-spacing-sm)

    *
        font-size var(--qmodii-text-sm)


    &-right
        margin-top auto

    &-label
        font-size var(--qmodii-text-sm)
        margin-right 4px
        white-space nowrap

    &-price
        margin 0 4px 0 2px
        white-space nowrap

    &-change
        white-space nowrap

        &.qmodii-miniquotes-change-positive
            color var(--qmodii-quote-positive-color)

        &.qmodii-miniquotes-change-negative
            color var(--qmodii-quote-negative-color)

.qmodii-data-source
    margin-right 2px
    display inline-flex
    align-items center

.qmodii-miniquotes-item-inside-chart
    .qmodii-extended-hours
        justify-content flex-start

.qmodii-miniquotes-extended-hours-small
    font-size 0.85em
    padding-top var(--qmodii-spacing-xs)
</style>
